import React, { useMemo, useState } from "react";

import { SpinnerIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { formatDate } from "@/utils/date";

import { usePostPerformance } from "../hooks/usePostPerformance";
import type {
  InsightsDisplayDTO,
  MetricValue,
  MetaPlatform,
  PaidDailyEntry,
  PostPerformanceContext,
  PostPerformanceSeriesEntry,
} from "../types";

interface PostPerformanceModalProps {
  postId: string | null;
  isOpen: boolean;
  onClose: () => void;
}

const WINDOW_OPTIONS = [7, 14, 30];

/**
 * Per-post performance modal.
 *
 * Renders the server-merged daily series (organic + paid) as a list
 * grouped by platform, each using a display component chosen off
 * `component_hint` (feed / reel / story). Context panels (follower
 * growth snapshot, demographics, best posting hours) sit below,
 * orthogonal to any single day's row.
 */
export const PostPerformanceModal: React.FC<PostPerformanceModalProps> = ({
  postId,
  isOpen,
  onClose,
}) => {
  const [windowDays, setWindowDays] = useState<number>(30);
  const { data, isLoading, error, refetch } = usePostPerformance(postId, windowDays, {
    enabled: isOpen,
  });

  const byPlatform = useMemo(() => groupByPlatform(data?.series ?? []), [data]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Post performance"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase">Window</span>
          {WINDOW_OPTIONS.map((days) => (
            <button
              key={days}
              onClick={() => setWindowDays(days)}
              className={`text-xs px-2 py-1 rounded border ${
                windowDays === days
                  ? "border-blue-500 bg-blue-50 text-blue-900"
                  : "border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {days}d
            </button>
          ))}
          <button
            onClick={() => refetch()}
            className="ml-auto text-xs text-blue-600 hover:text-blue-800 underline"
          >
            Refresh
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center gap-2 text-gray-600">
            <SpinnerIcon className="w-4 h-4" />
            Loading…
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error instanceof Error ? error.message : "Failed to load performance"}
          </div>
        )}

        {data && data.series.length === 0 && (
          <div className="rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No insights recorded in this window yet. New posts take a
            day or two to show data.
          </div>
        )}

        {data && data.series.length > 0 &&
          Object.entries(byPlatform).map(([platform, entries]) => (
            <PlatformSeries
              key={platform}
              platform={platform as MetaPlatform}
              entries={entries}
            />
          ))
        }

        {data && <ContextPanel context={data.context} />}
      </div>
    </Modal>
  );
};

// ---- Series per platform ------------------------------------------------

const PlatformSeries: React.FC<{
  platform: MetaPlatform;
  entries: PostPerformanceSeriesEntry[];
}> = ({ platform, entries }) => (
  <section className="space-y-2">
    <h3 className="text-sm font-semibold text-gray-900">
      {platform === "FACEBOOK" ? "Facebook" : "Instagram"}
    </h3>
    <div className="space-y-3">
      {entries.map((e) => (
        <DayRow key={`${e.platform}-${e.date}`} entry={e} />
      ))}
    </div>
  </section>
);

const DayRow: React.FC<{ entry: PostPerformanceSeriesEntry }> = ({ entry }) => {
  const date = formatDate(entry.date);
  return (
    <div className="rounded border border-gray-200 bg-white p-3 space-y-2">
      <div className="text-xs text-gray-500 font-medium">{date}</div>

      {entry.organic && <OrganicDisplay dto={entry.organic} />}
      {entry.paid && <PaidOverlay paid={entry.paid} />}
      {!entry.organic && !entry.paid && (
        <div className="text-xs text-gray-400">No data</div>
      )}
    </div>
  );
};

// ---- Display components per content type -------------------------------

const OrganicDisplay: React.FC<{ dto: InsightsDisplayDTO }> = ({ dto }) => {
  switch (dto.componentHint) {
    case "feed":
      return <FeedPostInsightsDisplay dto={dto} />;
    case "reel":
      return <ReelInsightsDisplay dto={dto} />;
    case "story":
      return <StoryInsightsDisplay dto={dto} />;
    default:
      return <FeedPostInsightsDisplay dto={dto} />;
  }
};

const FeedPostInsightsDisplay: React.FC<{ dto: InsightsDisplayDTO }> = ({ dto }) => (
  <div className="space-y-2">
    <PrimaryMetric metric={dto.primary} />
    <MetricBuckets dto={dto} />
  </div>
);

const ReelInsightsDisplay: React.FC<{ dto: InsightsDisplayDTO }> = ({ dto }) => (
  <div className="space-y-2">
    <PrimaryMetric metric={dto.primary} accent="Reel" />
    <MetricBuckets dto={dto} />
  </div>
);

const StoryInsightsDisplay: React.FC<{ dto: InsightsDisplayDTO }> = ({ dto }) => (
  <div className="space-y-2">
    <PrimaryMetric metric={dto.primary} accent="Story (single-snapshot)" />
    <MetricBuckets dto={dto} hideLabel="Traffic" />
  </div>
);

const MetricBuckets: React.FC<{
  dto: InsightsDisplayDTO;
  hideLabel?: string;
}> = ({ dto, hideLabel }) => (
  <div className="grid grid-cols-3 gap-3 text-xs">
    {dto.discovery.length > 0 && hideLabel !== "Discovery" && (
      <MetricBucket label="Discovery" metrics={dto.discovery} />
    )}
    {dto.engagement.length > 0 && hideLabel !== "Engagement" && (
      <MetricBucket label="Engagement" metrics={dto.engagement} />
    )}
    {dto.traffic.length > 0 && hideLabel !== "Traffic" && (
      <MetricBucket label="Traffic" metrics={dto.traffic} />
    )}
  </div>
);

const MetricBucket: React.FC<{ label: string; metrics: MetricValue[] }> = ({
  label,
  metrics,
}) => (
  <div>
    <div className="text-[10px] uppercase text-gray-400 mb-1">{label}</div>
    <div className="space-y-1">
      {metrics.map((m) => (
        <div key={m.key} className="flex justify-between text-gray-700" title={m.tooltip ?? undefined}>
          <span>{m.label}</span>
          <span className="font-medium text-gray-900">{formatValue(m.value)}</span>
        </div>
      ))}
    </div>
  </div>
);

const PrimaryMetric: React.FC<{ metric: MetricValue; accent?: string }> = ({
  metric,
  accent,
}) => (
  <div className="flex items-baseline gap-2">
    <span className="text-2xl font-semibold text-gray-900">
      {formatValue(metric.value)}
    </span>
    <span className="text-xs text-gray-500">{metric.label}</span>
    {accent && (
      <span className="ml-auto text-[10px] uppercase text-gray-400">{accent}</span>
    )}
  </div>
);

const PaidOverlay: React.FC<{ paid: PaidDailyEntry }> = ({ paid }) => (
  <div className="rounded bg-purple-50 border border-purple-100 px-3 py-2 text-xs text-purple-900">
    <div className="font-medium mb-1">Paid overlay</div>
    <div className="grid grid-cols-4 gap-2">
      <div>
        <div className="text-[10px] uppercase text-purple-500">Impressions</div>
        <div className="font-medium">{formatValue(paid.impressions)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-purple-500">Clicks</div>
        <div className="font-medium">{formatValue(paid.clicks)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-purple-500">Spend</div>
        <div className="font-medium">€{paid.spend.toFixed(2)}</div>
      </div>
      <div>
        <div className="text-[10px] uppercase text-purple-500">CTR</div>
        <div className="font-medium">
          {paid.ctr != null ? `${paid.ctr.toFixed(2)}%` : "—"}
        </div>
      </div>
    </div>
  </div>
);

// ---- Context panel (orthogonal page-level info) ------------------------

const ContextPanel: React.FC<{ context: PostPerformanceContext }> = ({ context }) => {
  const hasAny =
    context.followerSnapshot ||
    context.demographics ||
    context.audienceActivity;
  if (!hasAny) return null;

  return (
    <section className="rounded border border-gray-200 bg-gray-50 p-3 space-y-3 text-xs">
      <div className="text-[10px] uppercase text-gray-400">Context</div>

      {context.followerSnapshot && (
        <FollowerContext snapshot={context.followerSnapshot} />
      )}
      {context.demographics && <DemographicsContext demographics={context.demographics} />}
      {context.audienceActivity && (
        <div className="text-gray-600">
          Audience activity heatmap available (best posting hours) — rendered
          elsewhere.
        </div>
      )}
    </section>
  );
};

const FollowerContext: React.FC<{
  snapshot: NonNullable<PostPerformanceContext["followerSnapshot"]>;
}> = ({ snapshot }) => (
  <div>
    <div className="font-medium text-gray-800 mb-1">Follower growth</div>
    <table className="text-xs text-gray-700">
      <thead>
        <tr className="text-left">
          <th className="pr-3">Platform</th>
          <th className="pr-3">At publish</th>
          <th>Now</th>
        </tr>
      </thead>
      <tbody>
        {Object.keys({ ...snapshot.atPublish, ...snapshot.latest }).map((platform) => (
          <tr key={platform}>
            <td className="pr-3">{platform.toUpperCase()}</td>
            <td className="pr-3">{snapshot.atPublish[platform] ?? "—"}</td>
            <td>{snapshot.latest[platform] ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const DemographicsContext: React.FC<{
  demographics: Record<string, unknown>;
}> = ({ demographics }) => (
  <div>
    <div className="font-medium text-gray-800 mb-1">Demographics (monthly)</div>
    <div className="text-gray-600">
      Captured in the monthly report for the publish month. Full breakdown in Reporting.
    </div>
  </div>
);

// ---- Helpers ------------------------------------------------------------

function groupByPlatform(
  series: PostPerformanceSeriesEntry[],
): Record<string, PostPerformanceSeriesEntry[]> {
  const out: Record<string, PostPerformanceSeriesEntry[]> = {};
  for (const e of series) {
    (out[e.platform] ??= []).push(e);
  }
  return out;
}

function formatValue(v: number | null): string {
  if (v == null) return "—";
  return v.toLocaleString();
}
